import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Smartphone, Globe, Search, RotateCcw, Calendar, ChevronLeft, ChevronRight, Wifi, Activity, Tag } from 'lucide-react';
import { request } from '../../api/client';
import { cn } from '../../ui';
import { formatIst } from '../../lib/time';
import { todayStr } from '../../lib/dateRange';

interface InstallRow {
  id: string; name: string; phone: string | null; role: string;
  platform: string | null; platformInferred: boolean;
  appVersion: string | null; deviceModel: string | null; ip: string | null;
  lastSeen: string | null; activity: string;
}
interface ListResp { installs: InstallRow[]; pagination: { page: number; limit: number; total: number; pages: number } }
interface Stats { total: number; android: number; ios: number; web: number; active_last_30d: number; version_distribution: { version: string | null; count: number }[] }

const PAGE_SIZE = 25;
const TABS = [
  { id: 'all', label: 'All', params: {} as Record<string, string> },
  { id: 'android', label: 'Android', params: { platform: 'ANDROID' } },
  { id: 'ios', label: 'iOS', params: { platform: 'IOS' } },
  { id: 'web', label: 'Web', params: { platform: 'WEB' } },
  { id: 'active', label: 'Active (30d)', params: { activity: 'active' } },
  { id: 'stale', label: 'Stale', params: { activity: 'stale' } },
];
const ROLE_OPTIONS = ['', 'SUPER_ADMIN', 'ADMIN', 'ACCOUNTS', 'SALES_HEAD', 'SALES_PERSON', 'RETAILER', 'FARMER'];

function relTime(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function PlatformChip({ platform, inferred }: { platform: string | null; inferred: boolean }) {
  if (!platform) return <span className="text-xs italic text-muted">—</span>;
  const map: Record<string, { cls: string; icon: typeof Smartphone }> = {
    ANDROID: { cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', icon: Smartphone },
    IOS: { cls: 'bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-gray-200', icon: Smartphone },
    WEB: { cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: Globe },
  };
  const m = map[platform] ?? { cls: 'bg-surface-2 text-content', icon: Smartphone };
  const Icon = m.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded px-2 py-0.5 text-2xs font-semibold', m.cls)} title={inferred ? 'Inferred from the User-Agent (app did not report platform)' : undefined}>
      <Icon className="size-3" /> {platform === 'IOS' ? 'iOS' : platform.charAt(0) + platform.slice(1).toLowerCase()}{inferred && <span className="opacity-60">~</span>}
    </span>
  );
}

export function AppInstallsPage() {
  const [tabId, setTabId] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [role, setRole] = useState('');
  const [version, setVersion] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [tabId, debounced, role, version, fromDate, toDate]);

  const activeTab = useMemo(() => TABS.find((t) => t.id === tabId) || TABS[0], [tabId]);
  const params = useMemo(() => ({
    ...activeTab.params, page, limit: PAGE_SIZE, sort: 'desc',
    ...(debounced ? { search: debounced } : {}), ...(role ? { role } : {}), ...(version ? { version } : {}),
    ...(fromDate ? { from: `${fromDate} 00:00:00` } : {}), ...(toDate ? { to: `${toDate} 23:59:59` } : {}),
  }), [activeTab, page, debounced, role, version, fromDate, toDate]);

  const listQuery = useQuery({ queryKey: ['app-installs', params], placeholderData: keepPreviousData, queryFn: () => request<ListResp>({ url: '/app-installs', params }) });
  const statsQuery = useQuery({ queryKey: ['app-installs', 'stats'], staleTime: 60_000, queryFn: () => request<Stats>({ url: '/app-installs/stats' }) });

  const rows = listQuery.data?.installs ?? [];
  const pg = listQuery.data?.pagination ?? { page: 1, pages: 1, total: 0, limit: PAGE_SIZE };
  const st = statsQuery.data;

  function reset() { setSearch(''); setDebounced(''); setRole(''); setVersion(''); setFromDate(''); setToDate(''); setTabId('all'); }

  const inputCls = 'rounded-[10px] border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-500';
  const stats = [
    { label: 'Total (with sessions)', value: st?.total, color: 'text-content' },
    { label: 'Android', value: st?.android, color: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'iOS', value: st?.ios, color: 'text-content' },
    { label: 'Active (30d)', value: st?.active_last_30d, color: 'text-blue-600 dark:text-blue-400' },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="inline-flex items-center gap-2 text-xl font-semibold text-content"><Smartphone className="size-6 text-brand-600" /> App Installs</h1>
        <p className="mt-0.5 text-sm text-muted">Who's using the Android / iOS app (and web), their app version, device and last activity. Platform is reported by the app, or inferred from the User-Agent (marked ~).</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-card border border-line bg-surface p-4 shadow-soft">
            <div className="text-2xs uppercase tracking-wide text-muted">{s.label}</div>
            <div className={cn('mt-1 text-2xl font-bold tabular-nums', s.color)}>{s.value ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* Version distribution */}
      {!!st?.version_distribution?.length && (
        <div className="rounded-card border border-line bg-surface p-3 shadow-soft">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-content"><Tag className="size-3 text-brand-600" /> App version distribution</div>
            {version && <button onClick={() => setVersion('')} className="text-2xs text-muted hover:text-content">Clear (v{version})</button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {st.version_distribution.map((row) => {
              const isUnknown = !row.version; const active = version === (row.version || '');
              return (
                <button key={row.version || 'unknown'} disabled={isUnknown} onClick={() => !isUnknown && setVersion(active ? '' : row.version!)}
                  className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-2xs font-medium transition',
                    active ? 'border-brand-600 bg-brand-600 text-white' : isUnknown ? 'cursor-default border-line italic text-muted' : 'border-line text-content hover:bg-surface-2')}
                  title={isUnknown ? "Users we haven't seen a version for yet" : `Filter to v${row.version}`}>
                  <span className="font-mono">{row.version ? `v${row.version}` : 'unknown'}</span>
                  <span className={cn('font-bold', active ? 'text-white' : 'text-muted')}>{row.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface p-3 shadow-soft">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone…" className={cn(inputCls, 'w-full pl-9')} />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
          {ROLE_OPTIONS.map((r) => <option key={r || 'all'} value={r}>{r ? r.replace(/_/g, ' ') : 'All roles'}</option>)}
        </select>
        <div className="inline-flex items-center gap-1.5 text-xs text-muted">
          <Calendar className="size-3.5" />
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate || todayStr()} className={inputCls} aria-label="From date" />
          <span>→</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate || undefined} max={todayStr()} className={inputCls} aria-label="To date" />
        </div>
        <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-[10px] border border-line px-3 py-2 text-sm text-content hover:bg-surface-2"><RotateCcw className="size-3.5" /> Reset</button>
      </div>

      {/* Tabs + table */}
      <div className="rounded-card border border-line bg-surface shadow-soft">
        <div className="flex flex-wrap gap-1 overflow-x-auto border-b border-line px-2 py-2">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTabId(t.id)} className={cn('inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-sm font-medium transition', tabId === t.id ? 'border-brand-500 bg-brand-50 text-brand-800 dark:bg-brand-900/20 dark:text-brand-200' : 'border-transparent text-content hover:bg-surface-2')}>{t.label}</button>
          ))}
        </div>

        <div className="overflow-x-auto p-2">
          <table className="w-full min-w-[820px] text-sm">
            <thead><tr className="text-left text-2xs uppercase tracking-wide text-muted">
              <th className="px-3 py-2.5 font-semibold">User</th><th className="px-3 py-2.5 font-semibold">Role</th><th className="px-3 py-2.5 font-semibold">Platform</th><th className="px-3 py-2.5 font-semibold">Version</th><th className="px-3 py-2.5 font-semibold">Device</th><th className="px-3 py-2.5 font-semibold">Last seen</th><th className="px-3 py-2.5 font-semibold">Status</th>
            </tr></thead>
            <tbody>
              {listQuery.isLoading ? <tr><td colSpan={7} className="px-3 py-10 text-center text-muted">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="px-3 py-10 text-center text-muted">No users match these filters.</td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="border-t border-line hover:bg-surface-2/60">
                  <td className="px-3 py-2.5"><div className="text-sm font-medium text-content">{r.name || '—'}</div><div className="font-mono text-2xs text-muted">{r.phone || '—'}</div></td>
                  <td className="px-3 py-2.5"><span className="text-2xs uppercase tracking-widest text-muted">{r.role}</span></td>
                  <td className="px-3 py-2.5"><PlatformChip platform={r.platform} inferred={r.platformInferred} /></td>
                  <td className="px-3 py-2.5">{r.appVersion ? <span className="inline-flex items-center gap-1 rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-2xs font-semibold text-content">v{r.appVersion.replace(/^v/, '')}</span> : <span className="text-xs italic text-muted">unknown</span>}</td>
                  <td className="px-3 py-2.5"><span className="text-xs text-content">{r.deviceModel || <span className="italic text-muted">—</span>}</span></td>
                  <td className="px-3 py-2.5">{r.lastSeen ? <><div className="text-xs text-content">{formatIst(r.lastSeen)}</div><div className="text-2xs text-muted">{relTime(r.lastSeen)}</div></> : <span className="text-xs italic text-muted">never</span>}</td>
                  <td className="px-3 py-2.5">
                    {r.activity === 'active'
                      ? <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-2xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"><Wifi className="size-3" /> Active</span>
                      : <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-2xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"><Activity className="size-3" /> Stale</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3 flex items-center justify-between text-xs text-muted">
            <div>{pg.total ? `Showing ${(pg.page - 1) * PAGE_SIZE + 1}–${Math.min(pg.page * PAGE_SIZE, pg.total)} of ${pg.total}` : '0 users'}</div>
            <div className="inline-flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pg.page <= 1 || listQuery.isFetching} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-50"><ChevronLeft className="size-3.5" /> Prev</button>
              <span>Page {pg.page} / {pg.pages || 1}</span>
              <button onClick={() => setPage((p) => p + 1)} disabled={pg.page >= (pg.pages || 1) || listQuery.isFetching} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-50">Next <ChevronRight className="size-3.5" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
